const RESERVED_ORDER_STATES = ["waiting", "invoiced", "deposit", "completed"];
const INCOMING_RESUPPLY_ORDER_STATES = [
  "waiting",
  "invoiced",
  "deposit",
  "completed",
  "transit",
];
const RESERVED_ORDER_STATE_SET = new Set(RESERVED_ORDER_STATES);
const INCOMING_RESUPPLY_ORDER_STATE_SET = new Set(
  INCOMING_RESUPPLY_ORDER_STATES,
);

const getGenericDeviceStockItemId = (item) => item?.StockItemId || item?.id;
const getStockItemId = (stockRow) =>
  stockRow?.StockItemId || stockRow?.StockItem?.id;
const getStockWarehouseName = (stockRow) => stockRow?.Warehouse?.name;
const getCount = (item) => Number(item?.count || 0);
const getItemWarehouseKey = (stockItemId, warehouseName) =>
  [stockItemId, warehouseName].filter(Boolean).join(":");

const buildStockLookups = (stockRows) => {
  const rowsByItemId = new Map();
  const rowByItemWarehouse = new Map();

  (stockRows || []).forEach((stockRow) => {
    const stockItemId = getStockItemId(stockRow);
    if (!stockItemId) {
      return;
    }

    const stockItemKey = stockItemId.toString();
    if (!rowsByItemId.has(stockItemKey)) {
      rowsByItemId.set(stockItemKey, []);
    }
    rowsByItemId.get(stockItemKey).push(stockRow);

    const warehouseName = getStockWarehouseName(stockRow);
    if (warehouseName) {
      rowByItemWarehouse.set(
        getItemWarehouseKey(stockItemKey, warehouseName),
        stockRow,
      );
    }
  });

  return { rowsByItemId, rowByItemWarehouse };
};

const getPreferredIncomingStockRow = (stockRows, buyer) => {
  if (!stockRows?.length) {
    return;
  }
  if (stockRows.length === 1) {
    return stockRows[0];
  }

  const preferredWarehouses =
    buyer === "estonia"
      ? ["estonia", "mailhouse", "vantaa"]
      : ["mailhouse", "vantaa"];
  return (
    preferredWarehouses
      .map((warehouseName) =>
        stockRows.find(
          (stockRow) => getStockWarehouseName(stockRow) === warehouseName,
        ),
      )
      .find(Boolean) || stockRows[0]
  );
};

const findShipmentDevice = (shipment, stockItemKey) =>
  (shipment?.genericDevices || []).find(
    (device) =>
      getGenericDeviceStockItemId(device)?.toString() === stockItemKey,
  );

const addReservedQuantities = (stockRows, orders) => {
  const { rowsByItemId } = buildStockLookups(stockRows);
  (stockRows || []).forEach((stockRow) => {
    stockRow.reserved = 0;
  });

  (orders || []).forEach((order) => {
    if (!RESERVED_ORDER_STATE_SET.has(order?.paymentStatus)) {
      return;
    }

    (order.genericDevices || []).forEach((device) => {
      if (device?.type === "service") {
        return;
      }
      const stockItemId = getGenericDeviceStockItemId(device);
      if (!stockItemId) {
        return;
      }
      const stockRow = rowsByItemId.get(stockItemId.toString())?.[0];
      if (stockRow) {
        stockRow.reserved = (stockRow.reserved || 0) + getCount(device);
      }
    });
  });
};

const addIncomingQuantities = (stockRows, resupplyOrders, manufacturingOrders) => {
  const { rowsByItemId, rowByItemWarehouse } = buildStockLookups(stockRows);
  (stockRows || []).forEach((stockRow) => {
    stockRow.incoming = 0;
  });

  (resupplyOrders || []).forEach((order) => {
    if (!INCOMING_RESUPPLY_ORDER_STATE_SET.has(order?.paymentStatus)) {
      return;
    }

    (order.genericDevices || []).forEach((device) => {
      const stockItemId = getGenericDeviceStockItemId(device);
      if (!stockItemId) {
        return;
      }
      const stockItemKey = stockItemId.toString();
      const itemStockRows = rowsByItemId.get(stockItemKey) || [];
      const preferredStockRow = getPreferredIncomingStockRow(
        itemStockRows,
        order.buyer,
      );
      if (!preferredStockRow) {
        return;
      }

      preferredStockRow.incoming =
        (preferredStockRow.incoming || 0) + getCount(device);

      const preferredWarehouseName = getStockWarehouseName(preferredStockRow);
      const shipments = order.Shipments || [];
      const shippedElsewhereOrDelivered = shipments.reduce(
        (total, shipment) => {
          const shipmentDevice = findShipmentDevice(shipment, stockItemKey);
          if (!shipmentDevice) {
            return total;
          }
          if (shipment.status === "delivered") {
            return total + getCount(shipmentDevice);
          }
          if (
            (shipment.status === "transit" || shipment.status === "waiting") &&
            shipment.shippingInfo?.companyName !== preferredWarehouseName
          ) {
            return total + getCount(shipmentDevice);
          }
          return total;
        },
        0,
      );

      if (shippedElsewhereOrDelivered && preferredStockRow.incoming) {
        preferredStockRow.incoming =
          preferredStockRow.incoming - shippedElsewhereOrDelivered;
      }

      shipments.forEach((shipment) => {
        if (shipment.status !== "transit" && shipment.status !== "waiting") {
          return;
        }
        if (shipment.shippingInfo?.companyName === preferredWarehouseName) {
          return;
        }

        const shipmentDevice = findShipmentDevice(shipment, stockItemKey);
        const targetStockRow = rowByItemWarehouse.get(
          getItemWarehouseKey(stockItemKey, shipment.shippingInfo?.companyName),
        );
        if (shipmentDevice && targetStockRow) {
          targetStockRow.incoming += getCount(shipmentDevice);
        }
      });
    });
  });

  (manufacturingOrders || []).forEach((order) => {
    if (order?.status !== "progress") {
      return;
    }
    (order.ManufacturingOrderStockItems || []).forEach((item) => {
      const stockItemId = item.StockItemId ?? item.StockItem?.id;
      const warehouseId = order.WarehouseId ?? order.Warehouse?.id;
      const productionStockRow = (rowsByItemId.get(String(stockItemId)) || []).find(
        (row) => {
          const rowWarehouseId = row.WarehouseId ?? row.Warehouse?.id;
          if (warehouseId != null && rowWarehouseId != null) {
            return String(rowWarehouseId) === String(warehouseId);
          }
          return order.Warehouse?.name &&
            getStockWarehouseName(row) === order.Warehouse.name;
        },
      );
      // An existing production-site row takes priority, even with zero stock.
      const stockRow = productionStockRow || rowByItemWarehouse.get(
        getItemWarehouseKey(String(stockItemId), "mailhouse"),
      );
      const remaining = Math.max(
        Number(item.orderedQuantity || 0) - Number(item.producedQuantity || 0),
        0,
      );
      if (stockRow && Number.isFinite(remaining)) {
        stockRow.incoming += remaining;
      }
    });
  });
};

export {
  RESERVED_ORDER_STATES,
  INCOMING_RESUPPLY_ORDER_STATES,
  getStockWarehouseName,
  addReservedQuantities,
  addIncomingQuantities,
};
